import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";

/**
 * Minimal shape of a JSON schema property that the default view understands.
 * Only checkbox (boolean) and input (integer/number/string) properties are
 * rendered; anything else is skipped.
 */
export interface DefaultFeatureSchemaProperty {
  type?: string;
  description?: string;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  default?: unknown;
  const?: unknown;
  unit?: string;
  readOnly?: boolean;
}

export interface DefaultFeatureSchema {
  title?: string;
  featureName?: string;
  description?: string;
  properties?: Record<string, DefaultFeatureSchemaProperty>;
  required?: string[];
  requiredProperties?: string[];
}

interface RenderableProperty {
  key: string;
  label: string;
  kind: "checkbox" | "number" | "text";
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  default?: unknown;
  description?: string;
  unit?: string;
  readOnly: boolean;
  required: boolean;
}

/**
 * Generic component that renders a feature landing block for a schema for
 * which no dedicated feature view is implemented. Name, property details,
 * min/max and default values are fetched from the schema. Boolean properties
 * are rendered as checkboxes; integer and string properties are rendered as
 * an input field styled like the LumInfo input.
 */
@Component({
  standalone: false,
  selector: "app-default-feature-view",
  templateUrl: "./default-feature-view.component.html",
  styleUrls: ["./default-feature-view.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class DefaultFeatureViewComponent implements OnChanges {
  @Input() schema: DefaultFeatureSchema | null = null;
  @Input() model: Record<string, unknown> = {};
  @Input() featureSelected = false;
  @Input() disableCheckbox = false;
  @Input() collapsed = false;

  @Output() modelChange = new EventEmitter<Record<string, unknown>>();
  @Output() selectedChange = new EventEmitter<boolean>();
  @Output() restoreDefaults = new EventEmitter<void>();
  @Output() collapsedChange = new EventEmitter<boolean>();
  @Output() defaultChange = new EventEmitter<boolean>();
  @Output() validityChange = new EventEmitter<boolean>();

  featureName = "";
  description = "";
  isDefault = true;
  isValid = true;
  errorState: Record<string, boolean> = {};
  renderableProperties: RenderableProperty[] = [];
  private requiredKeys: Set<string> = new Set();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["schema"]) {
      this.parseSchema();
      this.applyDefaultsToModel();
    }
    if (changes["model"] && !changes["schema"]) {
      this.evaluateIsDefault();
      this.evaluateIsValid();
    }
  }

  trackByKey(_index: number, prop: RenderableProperty): string {
    return prop.key;
  }

  onValueChange(prop: RenderableProperty, rawValue: unknown): void {
    let value: unknown = rawValue;
    if (prop.kind === "number") {
      if (rawValue === "" || rawValue === null || rawValue === undefined) {
        value = "";
      } else {
        const parsed = Number(rawValue);
        value = Number.isNaN(parsed) ? "" : parsed;
      }
    }
    this.model = { ...(this.model ?? {}), [prop.key]: value };
    this.errorState = { ...this.errorState, [prop.key]: this.isPropertyInvalid(prop, value) };
    this.modelChange.emit(this.model);
    this.evaluateIsDefault();
    this.evaluateIsValid();
  }

  onIncludeChange(checked: boolean): void {
    this.selectedChange.emit(checked);
  }

  onRestoreDefault(): void {
    if (!this.renderableProperties.length) {
      return;
    }
    const next: Record<string, unknown> = { ...(this.model ?? {}) };
    for (const prop of this.renderableProperties) {
      if (prop.default !== undefined) {
        next[prop.key] = prop.default;
      }
    }
    this.model = next;
    this.modelChange.emit(this.model);
    this.evaluateIsDefault();
    this.evaluateIsValid();
    this.restoreDefaults.emit();
  }

  onToggleCollapse(): void {
    this.collapsed = !this.collapsed;
    this.collapsedChange.emit(this.collapsed);
  }

  private parseSchema(): void {
    const schema = this.schema ?? {};
    this.featureName = (schema.featureName ?? schema.title ?? "").toString();
    this.description = schema.description ?? "";
    this.requiredKeys = new Set<string>([
      ...(Array.isArray(schema.required) ? schema.required : []),
      ...(Array.isArray(schema.requiredProperties) ? schema.requiredProperties : []),
    ]);
    const properties = schema.properties ?? {};
    this.renderableProperties = Object.keys(properties)
      .map((key) => this.toRenderable(key, properties[key]))
      .filter((prop): prop is RenderableProperty => prop !== null);
  }

  private toRenderable(
    key: string,
    prop: DefaultFeatureSchemaProperty | undefined
  ): RenderableProperty | null {
    if (!prop || Object.prototype.hasOwnProperty.call(prop, "const")) {
      return null;
    }
    const type = (prop.type ?? "").toString().toLowerCase();
    let kind: RenderableProperty["kind"] | null = null;
    if (type === "boolean") {
      kind = "checkbox";
    } else if (type === "integer" || type === "number") {
      kind = "number";
    } else if (type === "string") {
      kind = "text";
    }
    if (!kind) {
      return null;
    }
    return {
      key,
      label: this.humanize(key),
      kind,
      min: typeof prop.minimum === "number" ? prop.minimum : undefined,
      max: typeof prop.maximum === "number" ? prop.maximum : undefined,
      minLength: typeof prop.minLength === "number" ? prop.minLength : undefined,
      maxLength: typeof prop.maxLength === "number" ? prop.maxLength : undefined,
      pattern: this.compilePattern(prop.pattern),
      default: prop.default,
      description: prop.description,
      unit: prop.unit,
      readOnly: prop.readOnly === true,
      required: this.requiredKeys.has(key),
    };
  }

  private compilePattern(pattern: string | undefined): RegExp | undefined {
    if (typeof pattern !== "string" || pattern.length === 0) {
      return undefined;
    }
    try {
      return new RegExp(pattern);
    } catch {
      return undefined;
    }
  }

  private humanize(key: string): string {
    return key ?? "";
  }

  private applyDefaultsToModel(): void {
    const current: Record<string, unknown> = { ...(this.model ?? {}) };
    let mutated = false;
    for (const prop of this.renderableProperties) {
      if (prop.default !== undefined && current[prop.key] === undefined) {
        current[prop.key] = prop.default;
        mutated = true;
      }
    }
    if (mutated) {
      this.model = current;
      this.modelChange.emit(this.model);
    }
    this.evaluateIsDefault();
    this.evaluateIsValid();
  }

  private evaluateIsDefault(): void {
    const previous = this.isDefault;
    if (!this.renderableProperties.length) {
      this.isDefault = true;
    } else {
      this.isDefault = this.renderableProperties.every((prop) => {
        if (prop.default === undefined) {
          return true;
        }
        const value = this.model?.[prop.key];
        return value === prop.default;
      });
    }
    if (previous !== this.isDefault) {
      this.defaultChange.emit(this.isDefault);
    }
  }

  private evaluateIsValid(): void {
    const previous = this.isValid;
    const nextErrors: Record<string, boolean> = {};
    let anyError = false;
    for (const prop of this.renderableProperties) {
      const invalid = this.isPropertyInvalid(prop, this.model?.[prop.key]);
      nextErrors[prop.key] = invalid;
      if (invalid) {
        anyError = true;
      }
    }
    this.errorState = nextErrors;
    this.isValid = !anyError;
    if (previous !== this.isValid) {
      this.validityChange.emit(this.isValid);
    }
  }

  private isPropertyInvalid(prop: RenderableProperty, rawValue: unknown): boolean {
    if (prop.kind === "checkbox") {
      return false;
    }
    const isEmpty = rawValue === null || rawValue === undefined || rawValue === "";
    if (isEmpty) {
      return prop.required;
    }
    if (prop.kind === "number") {
      return this.isNumberInvalid(prop, Number(rawValue));
    }
    return this.isTextInvalid(prop, String(rawValue));
  }

  private isNumberInvalid(prop: RenderableProperty, value: number): boolean {
    return !Number.isFinite(value)
      || (typeof prop.min === "number" && value < prop.min)
      || (typeof prop.max === "number" && value > prop.max);
  }

  private isTextInvalid(prop: RenderableProperty, str: string): boolean {
    if (typeof prop.minLength === "number" && str.length < prop.minLength) {
      return true;
    }
    if (typeof prop.maxLength === "number" && str.length > prop.maxLength) {
      return true;
    }
    if (prop.pattern && !prop.pattern.test(str)) {
      return true;
    }
    return false;
  }
}
